import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BookOfShadowsLayerConfig} from "../config/BookOfShadowsLayerConfig";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {BookOfShadowsGameConfig, IBookOfShadowsWinRatioConfig} from "../BookOfShadowsGameConfig";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BookOfShadowsGameModuleConfig} from "../BookOfShadowsGameModuleConfig";
import {SoundManager} from "../sound/SoundManager";
import {BookOfShadowsSoundConfig} from "../BookOfShadowsSoundConfig";

export class BookOfShadowsBigWinView extends BaseView {
    private _dimmer: PIXI.Graphics;
    private _fire: TimelineSprite;
    private _layer: ScalableLayer;
    private _activeItem: PIXI.spine.Spine | null;
    private _startTween: SpineTween;
    private _loopTween: SpineTween;

    /**
     * @constructs
     */
    constructor() {
        super();
        this._layer = StageManager.getLayer(BookOfShadowsLayerConfig.BIG_WIN.name);
        this._layer.addChild(this);
    }

    /**
     * @description To start the animation.
     * @param config
     * @param calledFromEnd
     */
    public getStartAnimation(config: IBookOfShadowsWinRatioConfig, calledFromEnd: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            spineName: string = "Big_Win_";

        let startTween: SpineTween,
            loopTween: SpineTween,
            item: PIXI.spine.Spine,
            index: number,
            level: string,
            dimmerTint: number,
            fromScale: number,
            toScale: number,
            delay: number;

        switch (config.text) {
            case "Big Win":
                index = 0;
                level = "1";
                dimmerTint = 0x009377;
                fromScale = 0;
                toScale = 1;
                delay = 0.25;
                break;
            case "Mega Win":
                index = 1;
                level = "2";
                dimmerTint = 0x8a0000;
                fromScale = 1;
                toScale = 1.3;
                delay = 0.5;
                break;
            case "Super Mega Win":
                index = 2;
                level = "3";
                this._fire.scale.set(2, 5);
                dimmerTint = 0x3a005f;
                fromScale = 1;
                toScale = 1.4;
                delay = 0.5;
                break;
            default:
                index = 0;
                level = "1";
                this._fire.scale.set(1);
                dimmerTint = 0x009377;
                fromScale = 0;
                toScale = 1;
                delay = 0.25;
                break;
        }

        this.killTween();
        if (this._activeItem) {
            this._activeItem.visible = false;
        }

        item = new PIXI.spine.Spine(GameResources.getSpineAsset(spineName + level));
        item.scale.set(0.5);
        item.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, 260);

        startTween = new SpineTween(item, 0, "BW" + level + "_Start", 0);
        loopTween = new SpineTween(item, 0, "BW" + level + "_Loop", -1);

        tl.kill();

        tl.add([
            () => this.configureItems(dimmerTint, item, index),
            startTween,
            () => {
                TweenLite.delayedCall(delay, () => BookOfShadowsGameModuleConfig.countUpView.playScaleAnimation(fromScale, toScale));
                TweenLite.delayedCall(delay, () => {
                    const newWidth: number = StageManager.stage.width * 1.1,
                        newHeight: number = StageManager.stage.height * 1.1,
                        width: number = StageManager.stage.width,
                        height: number = StageManager.stage.height;

                    TweenLite.fromTo(StageManager.stage.scale, 0.05, {x: 1, y: 1}, {
                        x: 1.1, y: 1.1, onComplete: () => {
                            TweenLite.to(StageManager.stage.scale, 0.05, {x: 1, y: 1})
                        }
                    });

                    TweenLite.fromTo(StageManager.stage, 0.05, {x: 0, y: 0}, {
                        x: -(newWidth - width) / 4, y: -(newHeight - height) / 4, onComplete: () => {
                            TweenLite.to(StageManager.stage, 0.05, {x: 0, y: 0})
                        }
                    });

                    TweenLite.fromTo(StageManager.stage, 0.05, {rotation: 0}, {
                        rotation: 0.05, onComplete: () => {
                            TweenLite.to(StageManager.stage, 0.05, {rotation: 0})
                        }
                    });
                });
            }
        ]);
        if (!calledFromEnd) {
            tl.add(TweenLite.to(() => {
            }, startTween.duration(), {}));
            tl.add(loopTween);
        }

        this._activeItem = item;
        this._startTween = startTween;
        this._loopTween = loopTween;

        return tl;
    }

    /**
     * @description End of big win.
     * @param config
     * @param displayDuration
     * @param fadeOutDuration
     */
    public end(config: IBookOfShadowsWinRatioConfig, displayDuration: number, fadeOutDuration: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (!this._activeItem || !this._activeItem.visible || this.children.length === 0) {
            tl.add(this.getStartAnimation(config, true));
        }
        tl.add(() => {
            for (let i: number = 1; i <= 3; i++) {
                SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_LEVEL + (i));
            }

            SoundManager.playBigWinEnd();
        });
        tl.add(this.fadeAndRemove(fadeOutDuration), displayDuration);

        return tl;
    }

    /**
     * @inheritDoc
     * @param config
     */
    public abort(config: IBookOfShadowsWinRatioConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (config.text === undefined || config.text === "") {
            return tl;
        }
        tl.add(() => {
            this.killTween();

            this.getStartAnimation(config, true);
        }, 0.001);

        return tl;
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        this.createDimmer();
        this.createFire();
    }

    /**
     * @inheritDoc
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);
        this._fire.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, NolimitApplication.screenBounds.bottom - 130);
    }

    /**
     * @description To create the dimmer for this view.
     */
    private createDimmer(): void {
        this._dimmer = new PIXI.Graphics();
        this._dimmer.beginFill(0xffffff, 0.6);
        this._dimmer.drawRect(-500, -500, 1680, 1680);
        this._dimmer.endFill();
        this._dimmer.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        this._dimmer.visible = false;
    }

    /**
     * @description To create the fire animation for this view.
     */
    private createFire(): void {
        this._fire = new TimelineSprite(GameResources.getTextures("bigWinFire"));
        this._fire.anchor.set(0.5);
        this._fire.visible = false;
        this._fire.blendMode = PIXI.BLEND_MODES.ADD;
    }

    /**
     * @description To configure the items used in this view.
     * @param dimmerTint
     * @param item
     * @param index
     */
    private configureItems(dimmerTint: number, item: PIXI.spine.Spine, index: number): void {
        this._dimmer.tint = dimmerTint;
        this._dimmer.visible = true;
        this._dimmer.alpha = 1;

        item.visible = true;
        item.alpha = 1;

        this.addChild(this._dimmer);
        this.addChild(this._fire);
        this.addChild(item);

        if (index !== 0) {
            SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_LEVEL + (index));
        }

        SoundManager.playBigWinLevel((index + 1));

        if (index === 2) {
            this._fire.visible = true;
            this._fire.alpha = 1;
            this._fire.playLoop();
        }

        BookOfShadowsGameModuleConfig.bigWinFountain.startFountain(index);
    }

    /**
     * @description This method removes the win item using the fade out effect.
     * @param duration
     */
    private fadeAndRemove(duration: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add([
            () => BookOfShadowsGameModuleConfig.bigWinFountain.stopFountain(),
            TweenLite.to(this._dimmer, duration, {alpha: 0, ease: Linear.easeNone}),
            TweenLite.to(this._fire, duration, {alpha: 0, ease: Linear.easeNone}),
            TweenLite.to(this._activeItem, duration, {
                alpha: 0,
                ease: Linear.easeNone,
                onComplete: () => this.allFaded()
            })
        ]);

        return tl;
    }

    /**
     * @description When all the items are faded away.
     */
    private allFaded(): void {
        this._dimmer.visible = false;
        this._fire.visible = false;
        this._activeItem!.visible = false;
        this._activeItem = null;
        this.children.length = 0;

        this.killTween();
    }

    /**
     * @description To kill the start and loop tween.
     */
    private killTween(): void {
        this._startTween?.kill();
        this._loopTween?.kill();
    }
}